import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer.js';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer.js';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';

import { useLayerRegistry } from '@/map/layers/registry';
import type { LegendEntry, LegendSwatchKind } from '@/map/layers/types';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  resolveFieldName,
} from './shared/layers';
import {
  geometrySwatchKind,
  parseColor,
  rgbToHex,
  symbolFor,
  type ClassificationSymbol,
} from './shared/classification-symbols';
import type { Tool } from './types';

type Input = {
  layer_id: string;
  field: string;
  value: string | number | Array<string | number>;
  color: string;
};

export const layerPaintByValue: Tool<Input> = {
  name: 'layer_paint_by_value',
  description:
    'Paint features whose `field` matches `value` with `color`, leaving every other feature in the layer\'s existing symbology untouched. Use this for "color the residential buildings red" — do NOT call `layer_classify` first, which would also recolor every other class. `value` accepts a single value or an array. `color` accepts hex (`#ff0000`) or common names (red, teal, blue, …). The original renderer is cached, so `layer_reset_symbology` restores it.',
  input_schema: {
    type: 'object',
    properties: {
      layer_id: { type: 'string' },
      field: { type: 'string' },
      value: {
        oneOf: [
          { type: 'string' },
          { type: 'number' },
          {
            type: 'array',
            items: { type: ['string', 'number'] },
            minItems: 1,
          },
        ],
        description: 'The attribute value (or list of values) to paint.',
      },
      color: {
        type: 'string',
        description: 'Hex color (e.g. "#ff0000") or a common name (red, teal, blue, …).',
      },
    },
    required: ['layer_id', 'field', 'value', 'color'],
    additionalProperties: false,
  },
  execute: async ({ layer_id, field, value, color }) => {
    const layer = await ensureFeatureLayer(layer_id);
    const record = requireLayerRecord(layer_id);
    const probe = record.probe;

    const resolvedField = resolveFieldName(field, probe, layer);
    if (!resolvedField) {
      throw new Error(
        `layer_paint_by_value: field "${field}" not found on layer "${layer_id}"`,
      );
    }

    const rgb = parseColor(color);
    if (!rgb) {
      throw new Error(
        `layer_paint_by_value: could not parse color "${color}". Use a hex code like "#ff0000" or a common name (red, teal, blue, …).`,
      );
    }

    const geometryType =
      (layer.geometryType as string | undefined) ?? probe?.service.geometry_type;
    const newSymbol = symbolFor(geometryType, rgb);
    if (!newSymbol) {
      throw new Error(
        `layer_paint_by_value: unsupported geometry type "${String(geometryType)}".`,
      );
    }
    const swatchKind = geometrySwatchKind(geometryType);

    const requested = Array.isArray(value) ? value : [value];
    if (requested.length === 0) {
      throw new Error('layer_paint_by_value: value must not be empty.');
    }

    const codedValues = probe?.fields.find((f) => f.name === resolvedField)?.coded_values;
    const labelFor = (v: string | number) => {
      const mapped = codedValues?.[String(v)];
      return mapped ?? String(v);
    };

    if (!record.original_renderer) {
      useLayerRegistry.getState().patch(layer_id, {
        original_renderer: layer.renderer ?? undefined,
      });
    }
    const baselineRenderer = record.original_renderer ?? layer.renderer ?? undefined;
    const defaultSymbol = pickDefaultSymbol(baselineRenderer, layer);

    const existingRenderer = layer.renderer;
    const existingLegend = record.legend;
    const reuseExisting =
      record.classified?.scheme === 'unique-values' &&
      record.classified.field === resolvedField &&
      existingRenderer instanceof UniqueValueRenderer &&
      existingLegend?.scheme === 'unique-values' &&
      existingLegend.field === resolvedField;

    let renderer: UniqueValueRenderer;
    let legendEntries: LegendEntry[];

    if (reuseExisting && existingRenderer instanceof UniqueValueRenderer) {
      const infos = [...(existingRenderer.uniqueValueInfos ?? [])];
      const entries = [...(existingLegend?.entries ?? [])];
      for (const v of requested) {
        const key = String(v);
        const idx = infos.findIndex((info) => String(info.value) === key);
        const label = labelFor(v);
        const symbolForEntry = newSymbol as unknown as (typeof infos)[number]['symbol'];
        if (idx >= 0) {
          infos[idx].symbol = symbolForEntry;
          infos[idx].label = label;
          entries[idx] = {
            ...entries[idx],
            label,
            value: key,
            color: rgbToHex(rgb),
            kind: (swatchKind ?? entries[idx]?.kind ?? 'fill') as LegendSwatchKind,
            index: idx,
          };
        } else {
          const newIdx = infos.length;
          infos.push({
            value: v,
            label,
            symbol: symbolForEntry,
          } as unknown as (typeof infos)[number]);
          entries.push({
            index: newIdx,
            label,
            value: key,
            color: rgbToHex(rgb),
            kind: (swatchKind ?? 'fill') as LegendSwatchKind,
          });
        }
      }
      const cloned = existingRenderer.clone();
      cloned.uniqueValueInfos =
        infos as unknown as UniqueValueRenderer['uniqueValueInfos'];
      renderer = cloned;
      legendEntries = entries;
    } else {
      const infos = requested.map((v) => ({
        value: v,
        label: labelFor(v),
        symbol: newSymbol,
      }));
      renderer = new UniqueValueRenderer({
        field: resolvedField,
        uniqueValueInfos: infos as unknown as UniqueValueRenderer['uniqueValueInfos'],
        defaultSymbol:
          defaultSymbol as unknown as UniqueValueRenderer['defaultSymbol'],
      });
      legendEntries = swatchKind
        ? requested.map((v, i) => ({
            index: i,
            label: labelFor(v),
            value: String(v),
            color: rgbToHex(rgb),
            kind: swatchKind,
          }))
        : [];
    }

    layer.renderer = renderer;
    useLayerRegistry.getState().patch(layer_id, {
      classified: { field: resolvedField, scheme: 'unique-values' },
      legend:
        legendEntries.length > 0 && swatchKind
          ? { field: resolvedField, scheme: 'unique-values', entries: legendEntries }
          : undefined,
    });

    return {
      ok: true,
      layer_id,
      field: resolvedField,
      painted: requested.map((v) => ({ value: String(v), label: labelFor(v) })),
      color: rgbToHex(rgb),
    };
  },
};

function pickDefaultSymbol(
  renderer: __esri.Renderer | undefined,
  layer: FeatureLayer,
): ClassificationSymbol | undefined {
  if (renderer instanceof SimpleRenderer && renderer.symbol) {
    return renderer.symbol as unknown as ClassificationSymbol;
  }
  if (renderer instanceof UniqueValueRenderer && renderer.defaultSymbol) {
    return renderer.defaultSymbol as unknown as ClassificationSymbol;
  }
  const fallback = (layer.renderer as { defaultSymbol?: ClassificationSymbol; symbol?: ClassificationSymbol } | null)
    ?? null;
  return fallback?.symbol ?? fallback?.defaultSymbol;
}
